import React from "react";
import { ArrowRight, Phone, MessageCircle, ExternalLink } from "lucide-react";
import AdMedia from "./AdMedia";

export const adImg = (a) => (!a?.image_url ? "" : a.image_url.startsWith("__abs__") ? a.image_url.slice(7) : `${process.env.REACT_APP_BACKEND_URL}${a.image_url}`);

export function ctaHref(a) {
  switch (a.cta_action) {
    case "url": return a.cta_url || "#";
    case "internal": return a.cta_url || "/my-portal";
    case "whatsapp": return `https://wa.me/${(a.whatsapp || a.phone || "").replace(/\D/g, "")}${a.title ? `?text=${encodeURIComponent(`Hi, I'm interested in "${a.title}"`)}` : ""}`;
    case "call": return `tel:${(a.phone || "").replace(/[^\d+]/g, "")}`;
    default: return null;
  }
}

const CtaIcon = ({ a }) => a.cta_action === "whatsapp" ? <MessageCircle size={14} /> : a.cta_action === "call" ? <Phone size={14} /> : a.cta_action === "url" ? <ExternalLink size={14} /> : <ArrowRight size={14} />;

export default function AdSlide({ a, slim = false, onCta, active = true, onMediaDone, onMediaShown }) {
  const img = adImg(a);
  const hasMedia = (a.media || []).length > 0;
  const Bg = ({ className = "" }) => hasMedia ? <AdMedia a={a} active={active} onDone={onMediaDone} onMediaShown={onMediaShown} className={className} /> : (img ? <img src={img} alt="" loading="lazy" decoding="async" className={`absolute inset-0 h-full w-full object-cover ${className}`} /> : null);
  const style = { background: `linear-gradient(135deg, ${a.bg_from || "#09090B"}, ${a.bg_to || "#3F3F46"})`, color: a.text_color || "#fff" };
  const href = ctaHref(a);
  const cta = a.cta_text && href ? (
    <a href={href} target={a.cta_action === "url" || a.cta_action === "whatsapp" ? "_blank" : undefined} rel="noreferrer" onClick={(e) => { e.stopPropagation(); onCta?.(); }} data-testid={`ad-cta-${a.id}`}
      className={`inline-flex items-center gap-1.5 font-semibold rounded-full bg-white/95 text-zinc-900 shadow-lg shadow-black/20 hover:bg-white hover:-translate-y-0.5 active:translate-y-0 transition-transform ${slim ? "text-[11px] px-3 py-1.5" : "text-xs sm:text-sm px-4 sm:px-5 py-2 sm:py-2.5"}`}>
      {a.cta_text} <CtaIcon a={a} />
    </a>
  ) : null;
  const badge = a.badge ? <span className={`inline-block rounded-full bg-white/15 backdrop-blur px-2.5 py-0.5 font-semibold tracking-[0.14em] uppercase ${slim ? "text-[8px]" : "text-[9px] sm:text-[10px]"}`}>{a.badge}</span> : null;

  if (slim) {
    return (
      <div className="relative h-full w-full overflow-hidden rounded-xl" style={style} data-testid={`ad-slide-${a.id}`}>
        {hasMedia ? <Bg className="opacity-60" /> : img ? <img src={img} alt="" loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover opacity-60" /> : null}
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-transparent" />
        <div className="relative h-full flex items-center gap-3 px-4 sm:px-10">
          <div className="flex-1 min-w-0">
            {badge}
            <div className="font-heading font-bold text-sm sm:text-base leading-tight truncate drop-shadow">{a.title || a.name}</div>
            {a.description ? <div className="text-[11px] opacity-85 truncate hidden sm:block">{a.description}</div> : null}
          </div>
          {cta}
        </div>
      </div>
    );
  }

  if (a.template === "image" && (img || hasMedia)) {
    return (
      <div className="relative h-full w-full overflow-hidden rounded-2xl bg-zinc-900" data-testid={`ad-slide-${a.id}`}>
        <Bg />
        {(a.title || cta) ? (
          <div className="absolute inset-x-0 bottom-0 p-4 sm:p-6 bg-gradient-to-t from-black/75 via-black/30 to-transparent text-white flex items-end justify-between gap-3">
            <div className="min-w-0">{badge}<div className="font-heading font-black text-lg sm:text-2xl leading-tight drop-shadow">{a.title}</div>{a.description ? <div className="text-xs sm:text-sm opacity-90 line-clamp-2 mt-0.5">{a.description}</div> : null}</div>
            {cta}
          </div>
        ) : null}
      </div>
    );
  }

  if (a.template === "offer") {
    return (
      <div className="relative h-full w-full overflow-hidden rounded-2xl" style={style} data-testid={`ad-slide-${a.id}`}>
        {hasMedia ? <Bg className="opacity-30 mix-blend-luminosity" /> : img ? <img src={img} alt="" loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover opacity-30 mix-blend-luminosity" /> : null}
        <div className="absolute -right-10 -top-10 h-48 w-48 rounded-full bg-white/10 blur-2xl" />
        <div className="relative h-full flex flex-col sm:flex-row items-center justify-center sm:justify-between gap-2 sm:gap-6 px-5 sm:px-10 text-center sm:text-left">
          <div className="font-heading font-black leading-none text-5xl sm:text-7xl tracking-tighter drop-shadow-lg">{a.badge || "OFFER"}</div>
          <div className="flex-1 min-w-0"><div className="font-heading font-bold text-lg sm:text-2xl leading-tight">{a.title}</div>{a.description ? <div className="text-xs sm:text-sm opacity-90 mt-1 line-clamp-2">{a.description}</div> : null}<div className="mt-3">{cta}</div></div>
        </div>
      </div>
    );
  }

  if (a.template === "business") {
    return (
      <div className="relative h-full w-full overflow-hidden rounded-2xl" style={style} data-testid={`ad-slide-${a.id}`}>
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, white 0, transparent 40%), radial-gradient(circle at 80% 70%, white 0, transparent 40%)" }} />
        <div className="relative h-full flex flex-col items-center justify-center text-center gap-2 px-6">
          {img ? <div className="h-16 w-16 sm:h-20 sm:w-20 rounded-2xl bg-white p-2 shadow-xl grid place-items-center"><img src={img} alt="" loading="lazy" className="max-h-full max-w-full object-contain" /></div> : null}
          <div className="font-heading font-black text-xl sm:text-3xl tracking-tight">{a.title || a.name}</div>
          {a.description ? <div className="text-xs sm:text-sm opacity-90 max-w-lg line-clamp-2">{a.description}</div> : null}
          <div className="mt-1">{cta}</div>
        </div>
      </div>
    );
  }

  if (a.template === "festival") {
    return (
      <div className="relative h-full w-full overflow-hidden rounded-2xl" style={style} data-testid={`ad-slide-${a.id}`}>
        <Bg />
        <div className="absolute inset-0 bg-gradient-to-br from-black/50 via-transparent to-black/60" />
        <div className="absolute inset-0 opacity-30 animate-pulse" style={{ backgroundImage: "radial-gradient(circle at 15% 20%, rgba(255,255,255,.6) 0 2px, transparent 3px), radial-gradient(circle at 70% 60%, rgba(255,255,255,.5) 0 2px, transparent 3px), radial-gradient(circle at 40% 80%, rgba(255,255,255,.5) 0 1.5px, transparent 2.5px)" }} />
        <div className="relative h-full flex flex-col items-center justify-center text-center gap-2 px-6">
          {badge}
          <div className="font-heading font-black text-2xl sm:text-4xl tracking-tight drop-shadow-lg">{a.title}</div>
          {a.description ? <div className="text-xs sm:text-base opacity-95 max-w-lg line-clamp-2">{a.description}</div> : null}
          <div className="mt-2">{cta}</div>
        </div>
      </div>
    );
  }

  // product / service (image side + text side)
  return (
    <div className="relative h-full w-full overflow-hidden rounded-2xl" style={style} data-testid={`ad-slide-${a.id}`}>
      <div className="h-full grid grid-cols-5">
        <div className="col-span-2 relative">
          {hasMedia ? <Bg /> : img ? <img src={img} alt="" loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover" /> : <div className="absolute inset-0 bg-white/10" />}
          <div className="absolute inset-y-0 right-0 w-10 bg-gradient-to-r from-transparent to-[rgba(0,0,0,.35)]" />
        </div>
        <div className="col-span-3 flex flex-col justify-center gap-1.5 px-4 sm:px-8 py-4">
          {badge}
          <div className="font-heading font-black text-lg sm:text-3xl leading-tight tracking-tight">{a.title || a.name}</div>
          {a.description ? <div className="text-xs sm:text-sm opacity-90 line-clamp-2 sm:line-clamp-3">{a.description}</div> : null}
          <div className="mt-2">{cta}</div>
        </div>
      </div>
    </div>
  );
}
